// TypeScript interfaces for Taxonomy Label Review feature

export interface TaxonomyLabel {
  label_name: string;
  confidence: number;
  rationale: string;
  regulation_reference?: string;
  dimension: string;
  category: string;
}

export type FeedbackSentiment = 'positive' | 'neutral' | 'negative';

export interface TaxonomyFeedback {
  classification_id: string;
  incorrect_labels: Array<{
    dimension: string;
    category: string;
    label: string;
  }>;
  missing_labels: Array<{
    dimension: string;
    category: string;
    label: string;
  }>;
  freeform_feedback?: {
    text: string;
    sentiment: FeedbackSentiment;
  };
}

export interface DimensionColors {
  [dimension: string]: string; // Tailwind color classes
}

export interface LabelTooltipProps {
  label: TaxonomyLabel;
  children: React.ReactNode;
}

export interface TaxonomyLabelProps {
  label: TaxonomyLabel;
  isIncorrect: boolean;
  onToggleIncorrect: () => void;
  color: string;
}

export interface DimensionSectionProps {
  dimension: string;
  labels: TaxonomyLabel[];
  color: string;
  incorrectLabels: Set<string>;
  onToggleIncorrect: (labelKey: string) => void;
  missingLabels: Array<{dimension: string; category: string; label: string}>;
  onAddMissingLabel: (label: {dimension: string; category: string; label: string}) => void;
  onRemoveMissingLabel: (index: number) => void;
}

export interface TaxonomyLabelReviewProps {
  classification_id: string;
  taxonomy_classification: any; // The hierarchical data structure from API
  onClose: () => void;
  // Signal context data
  signal_id?: string;
  signalSummary?: string;
  signalVerbatim?: string;
  customerName?: string;
  conductScore?: number;
}

// Note: Dimension colors and order are now fetched dynamically from the API
// via the useTaxonomySchema hook. This ensures we always show the correct
// dimensions for the current taxonomy version.

// Helper function to create unique label key
export const createLabelKey = (dimension: string, category: string, label: string): string => {
  return `${dimension}:${category}:${label}`;
};

// Helper function to parse label key
export const parseLabelKey = (key: string) => {
  const [dimension, category, label] = key.split(':');
  return { dimension, category, label };
};